import { createSlice } from '@reduxjs/toolkit';
import {
  changeEmailEffect,
  fetchApiKeyWebEffect,
  fetchBootstrapEffect,
  fetchPasswordPolicyEffect,
  signInEffect,
  verifyEmailEffect,
} from 'app/store/Auth/effects';
import { updateProfileEffect } from 'app/store/Profile/effects';
import { config } from 'config';
import { LoadingState } from 'types/enums';
import { BootstrapServices } from 'types/bootstrap';
import { Account, PasswordPolicy } from 'types/auth';
import { Time } from 'types/general.types';

export interface AuthState {
  authenticated: boolean;
  apiKeyWeb?: string;
  apiKeyLoadingState: LoadingState;
  services?: BootstrapServices;
  account?: Account;
  passwordPolicy?: PasswordPolicy;
  bootstrapLoadingState: LoadingState;
  lastLogin?: Time;
}

export const authInitialState: AuthState = {
  authenticated: false,
  bootstrapLoadingState: LoadingState.PRISTINE,
  apiKeyLoadingState: LoadingState.PRISTINE,
};

const slice = createSlice({
  name: 'auth',
  initialState: authInitialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchApiKeyWebEffect.fulfilled, (state, { payload }) => {
        state.apiKeyLoadingState = LoadingState.FETCHED;
        state.apiKeyWeb = payload;
      })
      .addCase(fetchApiKeyWebEffect.pending, (state) => {
        state.apiKeyLoadingState = LoadingState.FETCHING;
      })
      .addCase(fetchApiKeyWebEffect.rejected, (state, { error }) => {
        if (error.code === 'ECONNABORTED') {
          state.apiKeyLoadingState = LoadingState.PRISTINE;
          return;
        }
        state.apiKeyLoadingState = LoadingState.ERROR;
      })
      .addCase(fetchBootstrapEffect.pending, (state) => {
        state.bootstrapLoadingState = LoadingState.FETCHING;
      })
      .addCase(fetchBootstrapEffect.rejected, (state) => {
        state.bootstrapLoadingState = LoadingState.ERROR;
      })
      .addCase(fetchBootstrapEffect.fulfilled, (state, { payload }) => {
        state.services = payload.services;
        state.account = payload.account;
        state.authenticated = Boolean(payload.account);
        state.bootstrapLoadingState = LoadingState.FETCHED;
        state.lastLogin = payload.lastLogin as Time;
      })
      .addCase(fetchPasswordPolicyEffect.fulfilled, (state, action) => {
        state.passwordPolicy = action.payload;
      })
      .addCase(changeEmailEffect.fulfilled, (state, action) => {
        if (action.payload.verifyLink && state.services?.user?._links) {
          state.services.user._links.verifyEmail = action.payload.verifyLink;
        }
      })
      .addCase(updateProfileEffect.fulfilled, (state, { payload }) => {
        if (!payload || !state.account) {
          return;
        }
        state.account.displayName = payload.displayName;
      })
      .addCase(verifyEmailEffect.fulfilled, (state, { meta: { arg } }) => {
        if (state.account) {
          state.account.accountEmail = arg.newEmail;
        }
      })
      .addCase(signInEffect.fulfilled, (state, action) => {
        if (config.isElectronBuild || config.isFeatureBuild) {
          state.lastLogin = action.payload as Time;
        }
      });
  },
});

export const authReducer = slice.reducer;
